import { useCallback, useEffect, useRef, useState } from 'react';
import type { AxiosError } from 'axios';
import { Alert, CircularProgress, Modal, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDmpTranslation } from '@/hooks/useDmpTranslation';
import styled from 'styled-components';
import {
  downloadTranslation, getTranslationStatus, getTranslationCapabilities, getLatestTranslation,
  GetLanguagePair, Translate, TranslationCapabilities, TranslationLanguageOption, TranslationResultData,
} from '@/api/document.api';
import { Close } from '@/components/Icon';
import { Button } from '@/components/UI/Button';
import { getContentDispositionFilename } from '@/utils/contentDisposition';
import { getTranslationDownloadFailure } from '@/utils/translationDownloadError';
import { mapTranslationStatus, translationBlockReason, translationValidationMessage, TranslationValidationMessage, TranslationSource, TranslationView } from '@/utils/documentTranslation';
import { errorHandler, successHandler } from '@/utils/toastHandler';
import { dmpFeatureFailure, DmpFeatureFailure } from '@/utils/dmpFeatureFailure';
import FeatureFailure from '@/components/DmpV2/FeatureFailure';
import { TranslationStatusPanel } from '../style';

interface TranslateProps {
  document_id: string;
  source?: TranslationSource;
  show: boolean;
  setShowTranslate: (show: boolean) => void;
  getDocuments?: (id: number | null) => void;
  refreshDocuments?: () => void;
}
interface TranslationFormValues { fromLanguage: string; toLanguage: string }
interface TranslationErrorData { data?: { code?: string; job_id?: number; status?: string; errors?: { document?: string[]; quota?: string[] } } }
const Description = styled.p`
  margin: 0; font-size: ${(p) => p.theme.fontSizes.base}; line-height: 1.6; overflow-wrap: anywhere;
`;
const TranslationForm = styled.form`
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  width: min(640px, calc(100vw - 24px)); max-height: calc(100vh - 24px); max-height: calc(100dvh - 24px);
  display: flex; flex-direction: column; box-sizing: border-box; overflow: hidden;
  background: white; border-radius: 20px; color: ${(p) => p.theme.colors.gray500};
  &:focus { outline: none; }
  button { min-height: 44px; height: auto; white-space: normal; touch-action: manipulation; }
  button:focus-visible, select:focus-visible { outline: 2px solid ${(p) => p.theme.colors.primary500}; outline-offset: 3px; }
`;
const ScrollArea = styled.div`
  min-height: 0; overflow-y: auto; overscroll-behavior: contain; scrollbar-gutter: stable;
  margin: 8px 8px 0; padding: 20px 20px 28px;
  @media (max-width: 480px) { padding: 12px 8px 20px; }
`;
const Header = styled.header`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 16px;
  h2 { margin: 0; min-width: 0; font-size: ${(p) => p.theme.fontSizes.md}; line-height: 1.4; overflow-wrap: anywhere; }
`;
const CloseButton = styled.button`
  flex: 0 0 44px; width: 44px; padding: 10px; margin-top: -6px;
  border: 0; border-radius: 8px; background: transparent; cursor: pointer;
  &:hover { background: ${(p) => p.theme.colors.gray000}; }
  &:disabled { cursor: default; opacity: .5; }
  svg { width: 22px; height: 22px; }
`;
const Formats = styled.section`
  margin: 20px 0; padding: 16px; border-radius: 10px; background: ${(p) => p.theme.colors.gray000};
  font-size: ${(p) => p.theme.fontSizes.base}; line-height: 1.6; overflow-wrap: anywhere;
  h3 { margin: 0 0 4px; font-size: inherit; font-weight: 600; }
  p { margin: 0; }
`;
const LanguageFields = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; margin-top: 24px;
  > * { min-width: 0; }
  select { white-space: normal; min-height: 28px; overflow-wrap: anywhere; }
  @media (max-width: 520px) { grid-template-columns: minmax(0, 1fr); }
`;
const Actions = styled.footer`
  flex: 0 0 auto; display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 12px;
  border-top: 1px solid ${(p) => p.theme.colors.gray100}; padding: 20px 28px;
  button { min-width: 112px; min-height: 48px; padding: 10px 20px; border-radius: 8px; font-size: ${(p) => p.theme.fontSizes.base}; }
  @media (max-width: 480px) { padding: 16px; button { flex: 1 1 100px; min-width: 0; } }
`;
// 避免 CRA 展開整份舊版翻譯樹而超出 TypeScript 遞迴深度；新文案仍由小型 namespace 檢查。
const useLegacyTranslation = useTranslation as unknown as () => { t: (key: string) => string; i18n?: { language: string } };

const TranslateModal = ({ document_id, source, show, setShowTranslate, refreshDocuments }: TranslateProps) => {
  const { t, i18n } = useLegacyTranslation();
  const english = i18n?.language === 'en';
  const [featureFailure, setFeatureFailure] = useState<DmpFeatureFailure | null>(null);
  const { t: dt } = useDmpTranslation('documentTranslation');
  const [languages, setLanguages] = useState<TranslationLanguageOption[]>([]);
  const [capabilities, setCapabilities] = useState<TranslationCapabilities | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [initFailed, setInitFailed] = useState(false);
  const [validationMessage, setValidationMessage] = useState<TranslationValidationMessage | 'batchUnavailable' | null>(null);
  const [view, setView] = useState<TranslationView>('form');
  const [result, setResult] = useState<TranslationResultData | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const generation = useRef(0);
  const checking = useRef(false);
  const submitting = useRef(false);
  const refreshDocumentsRef = useRef(refreshDocuments);
  refreshDocumentsRef.current = refreshDocuments;
  const { handleSubmit, control, watch, reset } = useForm<TranslationFormValues>({ defaultValues: { fromLanguage: 'auto', toLanguage: '' } });
  const fromLanguage = watch('fromLanguage');
  const toLanguage = watch('toLanguage');
  const blockReason = translationBlockReason(source, capabilities);

  const applyResult = useCallback((next: TranslationResultData) => {
    setResult(next);
    setView(mapTranslationStatus(next.job.status));
    if (next.job.status === 'Succeeded') void refreshDocumentsRef.current?.();
  }, []);

  const initialize = useCallback(async () => {
    const current = ++generation.current;
    setInitializing(true); setInitFailed(false); setCapabilities(null); setResult(null); setView('form'); setPollCount(0);
    setValidationMessage(null); setFeatureFailure(null);
    checking.current = false; submitting.current = false; setIsChecking(false); setIsDownloading(false);
    reset({ fromLanguage: 'auto', toLanguage: '' });
    try {
      const [catalog, languageResponse, latest] = await Promise.all([getTranslationCapabilities(), GetLanguagePair(), getLatestTranslation(document_id)]);
      if (current !== generation.current) return;
      if (!catalog.data.success || !languageResponse.data.success || !latest.data.success) throw new Error('load_failed');
      setCapabilities(catalog.data.data); setLanguages(languageResponse.data.data);
      if (!catalog.data.data.auto_detect_source_language) reset({ fromLanguage: '', toLanguage: '' });
      if (latest.data.data) applyResult(latest.data.data);
    } catch {
      if (current === generation.current) setInitFailed(true);
    } finally { if (current === generation.current) setInitializing(false); }
  }, [document_id, reset, applyResult]);

  useEffect(() => {
    if (show && document_id) void initialize();
    return () => { generation.current += 1; };
  }, [show, document_id, initialize]);

  const closeModal = () => {
    if (submitting.current) return;
    generation.current += 1;
    setShowTranslate(false);
  };
  const resetForm = () => { setResult(null); setView('form'); setPollCount(0); setValidationMessage(null); setFeatureFailure(null); };
  const onSubmit = handleSubmit(async (data) => {
    if (featureFailure?.kind === 'reconciliation' || initializing || initFailed || blockReason || submitting.current || view !== 'form' || !data.fromLanguage || !data.toLanguage || data.fromLanguage === data.toLanguage) return;
    submitting.current = true;
    setValidationMessage(null); setFeatureFailure(null);
    const current = generation.current;
    setView('submitting'); setPollCount(0);
    try {
      const response = await Translate({ document_id, languagePairId: `${data.fromLanguage}-${data.toLanguage}`, from: data.fromLanguage, to: data.toLanguage });
      if (current !== generation.current) return;
      if (!response.data.success || !response.data.data?.job) throw new Error('unknown_result');
      applyResult(response.data.data);
      if (response.data.data.job.status === 'Succeeded') successHandler(t('success.translation'));
    } catch (error) {
      if (current !== generation.current) return;
      const response = (error as AxiosError<TranslationErrorData>).response;
      const details = response?.data?.data;
      const knownRejection = response?.status === 422 || response?.status === 401 || response?.status === 403
        || details?.code === 'DOCUMENT_BATCH_UNAVAILABLE';
      if (details?.job_id) {
        const status = details.status || 'ResultUnknown';
        setResult({ job: { id: details.job_id, status, source_lang: data.fromLanguage, target_lang: data.toLanguage, output_document_id: null }, output_document: null });
        setView(mapTranslationStatus(status));
      } else { setView(knownRejection ? 'form' : 'unknown'); }
      const classified = dmpFeatureFailure(error, english);
      if (classified.kind !== 'failed') {
        if (!details?.job_id) setView('form');
        setFeatureFailure(classified);
        errorHandler(classified.message);
        return;
      }
      const message = details?.code === 'DOCUMENT_BATCH_UNAVAILABLE' ? 'batchUnavailable' : translationValidationMessage(details?.errors?.document);
      if (knownRejection) setValidationMessage(message);
      errorHandler(knownRejection ? dt(message) : dt('unknown'));
    } finally { if (current === generation.current) submitting.current = false; }
  });

  const refreshStatus = useCallback(async () => {
    if (checking.current) return;
    checking.current = true; setIsChecking(true);
    const current = generation.current;
    try {
      const response = result?.job.id ? await getTranslationStatus(result.job.id) : await getLatestTranslation(document_id);
      if (current !== generation.current) return;
      if (!response.data.success) throw new Error('lookup_failed');
      if (response.data.data) applyResult(response.data.data);
      else setView('unknown');
    } catch {
      // A lookup failure does not prove the translation failed. Never invite a duplicate submission.
      if (current === generation.current) { setView('unknown'); errorHandler(dt('statusUnavailable')); }
    } finally {
      if (current === generation.current) { checking.current = false; setIsChecking(false); }
    }
  }, [result?.job.id, document_id, applyResult, dt]);

  useEffect(() => {
    if (!show || view !== 'running' || pollCount >= 30) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden' || checking.current) return;
      setPollCount((count) => count + 1);
      void refreshStatus();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [show, view, pollCount, refreshStatus]);

  const handleDownload = async () => {
    if (!result?.job.id || isDownloading) return;
    const current = generation.current;
    setIsDownloading(true);
    try {
      const response = await downloadTranslation(result.job.id);
      if (current !== generation.current) return;
      const filename = getContentDispositionFilename(response.headers['content-disposition']) || result.output_document?.file?.name || `translated-document-${result.job.id}`;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a'); link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
    } catch (error) {
      if (current !== generation.current) return;
      const failure = await getTranslationDownloadFailure(error);
      if (current !== generation.current) return;
      if (failure === 'expired') setView('expired');
      if (failure === 'notReady') { setView('running'); setPollCount(0); }
      errorHandler(t(failure === 'failed' ? 'pages.docLibrary.downloadFailed' : `pages.docLibrary.downloadErrors.${failure}`));
    } finally { if (current === generation.current) setIsDownloading(false); }
  };

  const sourceOptions = capabilities?.auto_detect_source_language ? [{ code: 'auto', name: dt('auto') }, ...languages] : languages;
  const statusText = {
    submitting: t('pages.docLibrary.submitting'), running: dt('running'), succeeded: t('pages.docLibrary.ready'),
    failed: t('pages.docLibrary.failed'), expired: t('pages.docLibrary.expired'), unknown: dt('unknown'), form: '',
  }[view];
  const formats = capabilities?.formats.map((format) => format.extension.replace(/^\./, '').toUpperCase()).join(' / ');

  const formError = validationMessage || blockReason;
  const autoPolling = view === 'running' && pollCount < 30;
  const statusLoading = view === 'submitting' || autoPolling || isChecking;
  const safeName = source?.name?.replace(/[\u0000-\u001f\u007f\u202a-\u202e\u2066-\u2069]/g, '') || dt('unnamedFile');

  return <Modal open={show} onClose={closeModal}>
    <TranslationForm onSubmit={onSubmit} role="dialog" aria-modal="true" aria-labelledby="translation-modal-title" aria-describedby="translation-file-name" tabIndex={-1}>
      <ScrollArea>
        <Header>
          <h2 id="translation-modal-title">{dt('title')}</h2>
          <CloseButton type="button" aria-label={dt('close')} onClick={closeModal} disabled={view === 'submitting'}><Close.CloseSVG aria-hidden="true" /></CloseButton>
        </Header>
        <FeatureFailure failure={featureFailure} english={english} />
        <Description id="translation-file-name" dir="auto">{safeName}</Description>
        {formats && <Formats aria-labelledby="translation-formats-title">
          <h3 id="translation-formats-title">{dt('supportedLabel')}</h3><p>{formats}</p>
        </Formats>}
        {initializing ? <Description role="status"><CircularProgress size={20} aria-hidden="true" sx={{ verticalAlign: 'middle', marginRight: 1 }} />{dt('loading')}</Description> : initFailed ? <Alert severity="error">{dt('loadFailed')}</Alert> : view === 'form' ? <>
          {formError && <Alert severity="warning">{dt(formError)}</Alert>}
          <LanguageFields>
            <Controller name="fromLanguage" control={control} render={({ field }) => <TextField {...field} select SelectProps={{ native: true }}
              id="translation-source-language" label={t('pages.docLibrary.fromLanguage')} fullWidth>
              <option value="" />{sourceOptions.map(item => <option key={item.code} value={item.code}>{item.name}</option>)}
            </TextField>} />
            <Controller name="toLanguage" control={control} render={({ field }) => <TextField {...field} select SelectProps={{ native: true }}
              id="translation-target-language" label={t('pages.docLibrary.toLanguage')} fullWidth>
              <option value="" />{languages.filter(item => item.code !== fromLanguage).map(item => <option key={item.code} value={item.code}>{item.name}</option>)}
            </TextField>} />
          </LanguageFields>
        </> : <>
          <TranslationStatusPanel data-status={view} role={['failed', 'expired', 'unknown'].includes(view) ? 'alert' : 'status'}>
            {statusLoading && <CircularProgress size={20} aria-hidden="true" sx={{ verticalAlign: 'middle', marginRight: 1 }} />}{statusText}
          </TranslationStatusPanel>
          {view === 'running' && pollCount >= 30 && <Description>{dt('pollPaused')}</Description>}
        </>}
      </ScrollArea>
      <Actions>
        <Button type="button" variant="primary-invert" onClick={closeModal} disabled={view === 'submitting'}>{dt('cancel')}</Button>
        {!initializing && !initFailed && view === 'form' && formError === 'batchUnavailable' &&
          <Button type="button" variant="primary-invert" onClick={initialize}>{dt('retry')}</Button>}
        {!initializing && (initFailed ? <Button type="button" variant="primary" onClick={initialize}>{dt('retry')}</Button> : view === 'form' ?
          <Button type="submit" variant="primary" disabled={featureFailure?.kind === 'reconciliation' || !!blockReason || !fromLanguage || !toLanguage || fromLanguage === toLanguage}>{dt('start')}</Button> : <>
            {['succeeded', 'failed', 'expired'].includes(view) && <Button type="button" variant="primary-invert" onClick={resetForm}>{view === 'succeeded' ? dt('newTranslation') : t('pages.docLibrary.startAgain')}</Button>}
            {view === 'succeeded' && <Button type="button" variant="primary" onClick={handleDownload} disabled={isDownloading}>{isDownloading ? t('pages.docLibrary.downloading') : t('buttons.download')}</Button>}
            {(view === 'running' || view === 'unknown') && <Button type="button" variant="primary" onClick={refreshStatus} disabled={autoPolling || isChecking} aria-busy={autoPolling || isChecking}>
              {autoPolling || isChecking ? <><CircularProgress size={18} color="inherit" aria-hidden="true" sx={{ marginRight: 1 }} />{dt('processing')}</> : t('pages.docLibrary.checkStatus')}
            </Button>}
          </>)}
      </Actions>
    </TranslationForm>
  </Modal>;
};
export default TranslateModal;
